import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { ArchitecturalPlanServiceClient } from '../../generated.commonjs/ApiServiceClientPb';
import {
  ProjectMetadata,
  ProjectAddress,
  ProjectIngestionSettings,
  CreateProjectMetadataRequest,
  CreateProjectMetadataResponse,
  UpdateProjectMetadataRequest,
  UpdateProjectMetadataResponse,
} from '../../generated.commonjs/api_pb';
import {
  ProjectModelConfig as ProjectModelConfigProto,
  UseCaseModelAssignment as UseCaseModelAssignmentProto,
  LlmApplicationUseCase,
} from '../../generated.commonjs/model_config_pb';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';

/**
 * Service for managing project metadata (Issue #227 - Phase 1).
 * Wraps the new gRPC metadata RPCs.
 * Follows the same pattern as TaskService, FileMetadataService, etc.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectMetadataService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private grpcWrapper = inject(GrpcWrapperService);

  private static instanceCounter = 0;
  private instanceId: number;
  private client: ArchitecturalPlanServiceClient;

  constructor() {
    this.instanceId = ++ProjectMetadataService.instanceCounter;
    ClientLoggingService.log('INFO', `[ProjectMetadataService ${this.instanceId}] Initialized`);

    // Initialize gRPC client with authentication (same pattern as other services)
    const apiServer = this.environmentService.getApiServer();
    ClientLoggingService.log('INFO', `[ProjectMetadataService ${this.instanceId}] Using gRPC API server: ${apiServer}`);

    // Configure gRPC client with auth interceptor
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };

    this.client = new ArchitecturalPlanServiceClient(`https://${apiServer}:443`, null, options);
    ClientLoggingService.log('INFO', `[ProjectMetadataService ${this.instanceId}] Client created`);
  }

  /**
   * Creates project metadata for a legacy project (Story 3).
   */
  async createProjectMetadata(
    projectId: string,
    projectName: string,
    projectDescription: string
  ): Promise<CreateProjectMetadataResponse> {
    ClientLoggingService.log(
      'INFO',
      `[ProjectMetadataService ${this.instanceId}] Creating metadata for project: ${projectId}`
    );

    try {
      const metadata = new ProjectMetadata();
      metadata.setProjectName(projectName);
      metadata.setProjectDescription(projectDescription);

      const request = new CreateProjectMetadataRequest();
      request.setArchitecturalPlanId(projectId);
      request.setMetadata(metadata);

      ClientLoggingService.log(
        'INFO',
        `[ProjectMetadataService ${this.instanceId}] Calling createProjectMetadata RPC...`
      );

      // Use promise-based call (same pattern as other services)
      const response = await this.client.createProjectMetadata(request, null);

      ClientLoggingService.log(
        'INFO',
        `[ProjectMetadataService ${this.instanceId}] CreateProjectMetadata success:`,
        response.toObject()
      );
      return response;
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[ProjectMetadataService ${this.instanceId}] CreateProjectMetadata error:`,
        error
      );
      throw error;
    }
  }

  /**
   * Updates existing project metadata (Stories 1 & 2, Issue #1020).
   */
  async updateProjectMetadata(
    projectId: string,
    updates: Partial<{
      projectName: string;
      projectDescription: string;
      projectAddress: {
        street: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        googlePlaceId?: string;
        latitude?: number;
        longitude?: number;
        formattedAddress?: string;
      };
      modelConfig: {
        useCaseOverrides: Array<{
          useCase: string;
          profileId: string;
          roleProfiles: Record<string, string>;
        }>;
      };
      ingestionSettings: {
        enableVisualVerification?: boolean;
        enableRegionHierarchy?: boolean;
      };
      powerUserMode: boolean;
    }>
  ): Promise<UpdateProjectMetadataResponse> {
    ClientLoggingService.log(
      'INFO',
      `[ProjectMetadataService ${this.instanceId}] Updating metadata for project: ${projectId}`
    );

    try {
      const metadata = new ProjectMetadata();

      if (updates.projectName !== undefined) {
        metadata.setProjectName(updates.projectName);
      }
      if (updates.projectDescription !== undefined) {
        metadata.setProjectDescription(updates.projectDescription);
      }
      if (updates.projectAddress !== undefined) {
        const address = new ProjectAddress();
        address.setStreet(updates.projectAddress.street);
        address.setCity(updates.projectAddress.city);
        address.setState(updates.projectAddress.state);
        address.setPostalCode(updates.projectAddress.postalCode);
        address.setCountry(updates.projectAddress.country);

        // Set Google Maps metadata (Issue #236)
        if (updates.projectAddress.googlePlaceId) {
          address.setGooglePlaceId(updates.projectAddress.googlePlaceId);
        }
        if (updates.projectAddress.latitude !== undefined) {
          address.setLatitude(updates.projectAddress.latitude);
        }
        if (updates.projectAddress.longitude !== undefined) {
          address.setLongitude(updates.projectAddress.longitude);
        }
        if (updates.projectAddress.formattedAddress) {
          address.setFormattedAddress(updates.projectAddress.formattedAddress);
        }

        metadata.setProjectAddress(address);
      }

      if (updates.modelConfig !== undefined) {
        const modelConfig = new ProjectModelConfigProto();
        const assignments = updates.modelConfig.useCaseOverrides
          .filter(override => {
            const useCaseEnum = LlmApplicationUseCase[override.useCase as keyof typeof LlmApplicationUseCase];
            if (useCaseEnum === undefined) {
              ClientLoggingService.log(
                'WARN',
                `[ProjectMetadataService] Unknown use case enum '${override.useCase}'; skipping`
              );
              return false;
            }
            return true;
          })
          .map(override => {
            const assignment = new UseCaseModelAssignmentProto();
            const useCaseEnum = LlmApplicationUseCase[override.useCase as keyof typeof LlmApplicationUseCase];
            assignment.setUseCase(useCaseEnum!);
            assignment.setProfileId(override.profileId);
            if (override.roleProfiles) {
              const roleMap = assignment.getRoleProfilesMap();
              for (const [role, profileId] of Object.entries(override.roleProfiles)) {
                roleMap.set(role, profileId);
              }
            }
            return assignment;
          });
        modelConfig.setUseCaseOverridesList(assignments);
        metadata.setModelConfig(modelConfig);
      }

      if (updates.ingestionSettings !== undefined) {
        const settings = new ProjectIngestionSettings();
        if (updates.ingestionSettings.enableVisualVerification !== undefined) {
          settings.setEnableVisualVerification(updates.ingestionSettings.enableVisualVerification);
        }
        if (updates.ingestionSettings.enableRegionHierarchy !== undefined) {
          settings.setEnableRegionHierarchy(updates.ingestionSettings.enableRegionHierarchy);
        }
        metadata.setIngestionSettings(settings);
      }

      if (updates.powerUserMode !== undefined) {
        metadata.setPowerUserMode(updates.powerUserMode);
      }

      const request = new UpdateProjectMetadataRequest();
      request.setArchitecturalPlanId(projectId);
      request.setMetadata(metadata);

      ClientLoggingService.log(
        'INFO',
        `[ProjectMetadataService ${this.instanceId}] Calling updateProjectMetadata RPC...`
      );

      // Use promise-based call (same pattern as other services)
      const response = await this.client.updateProjectMetadata(request, null);

      ClientLoggingService.log(
        'INFO',
        `[ProjectMetadataService ${this.instanceId}] UpdateProjectMetadata success:`,
        response.toObject()
      );
      return response;
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[ProjectMetadataService ${this.instanceId}] UpdateProjectMetadata error:`,
        error
      );
      throw error;
    }
  }
}
